import React, { useEffect } from 'react';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { useInViewAnimation } from '../hooks/useInViewAnimation';
import { useLiveStatus } from '../hooks/useLiveStatus';
import { ProjectGrid } from './Showcase';
import {
  productProjects,
  systemProjects,
  mobileProjects,
  shippedProjects,
  inProgressProjects,
} from '../data/projects';
import { caseStudyIndex } from '../data/caseStudyIndex';

// Full project list, split by ownership rather than by technology. The old
// three-way split (Software / AI systems / Web3) overlapped so heavily that
// the same project could have sat in any of the buckets.

const GROUPS = [
  {
    kicker: 'Products I own',
    heading: 'Products',
    lede: 'Things I built, launched, and still operate.',
    items: productProjects,
  },
  {
    kicker: 'iOS and Android',
    heading: 'Mobile apps',
    lede: 'One live, three being built. Badges say where each one actually is, and anything showing sample or prototype data is labelled.',
    items: mobileProjects,
  },
  {
    kicker: 'Built to run inside other stacks',
    heading: 'Systems',
    lede: 'Infrastructure and agents that sit behind someone else’s product.',
    items: systemProjects,
  },
];

// The write-ups live on their own page. /work stays a catalogue; this strip is
// the way across for anyone who wants the engineering.
const CaseStudiesBanner = ({ onNavigateCaseStudies, onNavigateAutomations }) => (
  <section className="max-w-[1200px] mx-auto px-6 pt-6 space-y-3" data-testid="case-studies-banner">
    <button
      type="button"
      onClick={onNavigateCaseStudies}
      className="group w-full text-left flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-[#F6FCFF] border border-[#E0EBF0] rounded-2xl px-6 py-5 hover:border-[#273C46]/30 transition-colors"
    >
      <span>
        <span className="block font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-1">How they were built</span>
        <span className="block text-[#051A24]">
          {caseStudyIndex.length} case studies: the problem, the architecture, the decisions, and the numbers.
        </span>
      </span>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#051A24] group-hover:gap-2.5 transition-all">
        Read the case studies <ArrowRight className="w-4 h-4" />
      </span>
    </button>

    <button
      type="button"
      onClick={onNavigateAutomations}
      className="group w-full text-left flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-[#051A24] text-[#F6FCFF] rounded-2xl px-6 py-5 hover:bg-[#0A2836] transition-colors"
      data-testid="automations-banner"
    >
      <span>
        <span className="block font-mono text-xs uppercase tracking-[0.2em] text-[#9FC4D2] mb-1">Automations</span>
        <span className="block">
          The pipelines behind the products: triggers, steps, and where a person still signs off.
        </span>
      </span>
      <span className="inline-flex items-center gap-1.5 text-sm font-medium group-hover:gap-2.5 transition-all">
        See the workflows <ArrowRight className="w-4 h-4" />
      </span>
    </button>
  </section>
);

const Group = ({ group, onNavigateCaseStudy }) => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section ref={ref} className="max-w-[1200px] mx-auto px-6 py-14">
      <div className="mb-10">
        <p
          className={`font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3 ${
            isInView ? 'animate-fade-in-up' : 'opacity-0'
          }`}
        >
          {group.kicker}
        </p>
        <h2
          className={`text-3xl md:text-4xl text-[#051A24] mb-3 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
        >
          {group.heading}
        </h2>
        <p
          className={`text-[#273C46] max-w-xl ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.15s' }}
        >
          {group.lede}
        </p>
      </div>

      <ProjectGrid items={group.items} isInView={isInView} onNavigateCaseStudy={onNavigateCaseStudy} />
    </section>
  );
};

export const WorkPage = ({
  onNavigateHome,
  onOpenContact,
  onNavigateCaseStudy,
  onNavigateCaseStudies,
  onNavigateAutomations,
}) => {
  const { liveCount, checkedAt } = useLiveStatus();

  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = 'Work — Brandon Crenshaw';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="work-page">
      <div className="max-w-[1200px] mx-auto px-6 pt-10">
        <button
          type="button"
          onClick={onNavigateHome}
          className="inline-flex items-center gap-2 text-sm text-[#273C46] hover:text-[#051A24] transition-colors py-2"
          data-testid="work-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </button>
      </div>

      <header className="max-w-[1200px] mx-auto px-6 pt-8 pb-4">
        <h1 className="text-4xl md:text-5xl text-[#051A24] mb-4">
          Everything I've{' '}
          <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
            shipped
          </span>
        </h1>
        <p className="text-[#273C46] max-w-2xl text-lg">
          {liveCount === null
            ? `${shippedProjects.length} products and systems shipped, all linked.`
            : `${shippedProjects.length} products and systems shipped. ${liveCount} answered when the checker last ran${
                checkedAt ? ` on ${new Date(checkedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''
              }.`}{' '}
          Click any card to use the real thing.
          {inProgressProjects.length > 0 && ` ${inProgressProjects.length} more are in progress below.`}
        </p>
      </header>

      <CaseStudiesBanner onNavigateCaseStudies={onNavigateCaseStudies} onNavigateAutomations={onNavigateAutomations} />

      {GROUPS.map((group) => (
        <Group key={group.heading} group={group} onNavigateCaseStudy={onNavigateCaseStudy} />
      ))}

      <section className="max-w-[1200px] mx-auto px-6 py-16">
        <div className="bg-[#051A24] rounded-3xl p-12 md:p-16 text-center">
          <h2 className="text-2xl md:text-3xl text-white mb-4">Want something like this built?</h2>
          <p className="text-[#E0EBF0]/80 mb-8 max-w-lg mx-auto">
            Tell me what you're working on and I'll tell you the shortest path to shipping it.
          </p>
          <button
            type="button"
            onClick={onOpenContact}
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-[#051A24] text-sm font-medium hover:bg-[#F6FCFF] transition-colors"
            data-testid="work-cta-btn"
          >
            Book a 20 minute call <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
};

export default WorkPage;
